import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Smartphone, ChevronRight, ChevronLeft, CheckCircle, AlertCircle, 
  Phone, User, MapPin, Shield, Clock, Banknote, Package, 
  Zap, Battery, Camera, Volume2, Fingerprint, Wifi, X,
  Monitor, CircuitBoard, Settings, Box, Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Zobox Brand Colors
const ZOBOX_BLUE = '#2b3380';

// Customer QC Questions mapped to PQC parameters
const CUSTOMER_QC_FLOW = [
  {
    id: 'device_working',
    title: 'Is your phone switching on?',
    subtitle: 'Select the current state of your device',
    icon: Zap,
    pqc_param: 'power_status',
    options: [
      { 
        id: 'working', 
        text: 'Yes, working fine',
        description: 'Phone turns on and functions normally',
        image: '📱',
        pqc_option: 'pow_1',
        severity: 'Good'
      },
      { 
        id: 'dead', 
        text: 'No, phone is dead',
        description: 'Phone does not turn on at all',
        image: '📵',
        pqc_option: 'pow_2',
        severity: 'Salvage'
      }
    ]
  },
  {
    id: 'screen_condition',
    title: 'How is your screen?',
    subtitle: 'Select the best description of your screen',
    icon: Monitor,
    pqc_param: 'display_condition',
    options: [
      { 
        id: 'perfect', 
        text: 'No issues',
        description: 'Screen is flawless with no damage',
        image: '✨',
        pqc_option: 'dsp_1',
        severity: 'Good'
      },
      { 
        id: 'minor_scratches', 
        text: 'Minor scratches',
        description: 'Small scratches only visible under light',
        image: '〰️',
        pqc_option: 'scr_2',
        severity: 'Low'
      },
      { 
        id: 'heavy_scratches', 
        text: 'Heavy scratches',
        description: 'Multiple visible scratches on screen',
        image: '📐',
        pqc_option: 'scr_3',
        severity: 'Medium'
      },
      { 
        id: 'cracked', 
        text: 'Cracked / Broken',
        description: 'Glass is cracked or shattered',
        image: '💥',
        pqc_option: 'scr_4',
        severity: 'High'
      }
    ]
  },
  {
    id: 'screen_defects',
    title: 'Any display defects?',
    subtitle: 'Check for spots, lines, or discoloration',
    icon: Monitor,
    pqc_param: 'display_defects',
    conditional: { param: 'screen_condition', notValues: ['cracked'] },
    options: [
      { 
        id: 'no_defects', 
        text: 'No defects',
        description: 'Display is clear with no issues',
        image: '✅',
        pqc_options: { 'screen_fading': 'fade_1', 'spots_patch': 'spot_1' },
        severity: 'Good'
      },
      { 
        id: 'minor_spots', 
        text: 'Minor spots/fading',
        description: 'Small spots or slight discoloration',
        image: '🔵',
        pqc_options: { 'screen_fading': 'fade_2', 'spots_patch': 'spot_2' },
        severity: 'Low'
      },
      { 
        id: 'lines_issues', 
        text: 'Lines / Dead pixels',
        description: 'Visible lines or dead pixels on screen',
        image: '📊',
        pqc_options: { 'display_condition': 'dsp_2' },
        severity: 'High'
      }
    ]
  },
  {
    id: 'touch_working',
    title: 'Is touch working properly?',
    subtitle: 'Test all areas of your screen',
    icon: Fingerprint,
    pqc_param: 'touch_panel',
    options: [
      { 
        id: 'touch_ok', 
        text: 'Yes, fully working',
        description: 'Touch responds in all areas',
        image: '👆',
        pqc_option: 'tch_1',
        severity: 'Good'
      },
      { 
        id: 'touch_partial', 
        text: 'Partial / Ghost touch',
        description: 'Some areas not responding or auto touches',
        image: '👻',
        pqc_option: 'tch_2',
        severity: 'High'
      },
      { 
        id: 'touch_not_working', 
        text: 'Not working',
        description: 'Touch does not respond at all',
        image: '🚫',
        pqc_option: 'tch_3',
        severity: 'High'
      }
    ]
  },
  {
    id: 'phone_lock',
    title: 'Is your phone locked?',
    subtitle: 'Check for any account or finance locks',
    icon: Shield,
    pqc_param: 'phone_lock',
    options: [
      { 
        id: 'no_lock', 
        text: 'No lock',
        description: 'Phone is not locked to any account',
        image: '🔓',
        pqc_option: 'lock_1',
        severity: 'Good'
      },
      { 
        id: 'finance_locked', 
        text: 'Finance / EMI locked',
        description: 'Phone is locked due to pending EMI',
        image: '🏦',
        pqc_option: 'lock_2',
        severity: 'Salvage'
      },
      { 
        id: 'account_locked', 
        text: 'Google/Samsung locked',
        description: 'Phone is locked to an account',
        image: '🔐',
        pqc_option: 'lock_2',
        severity: 'Salvage'
      }
    ]
  },
  {
    id: 'battery_health',
    title: 'How is the battery?',
    subtitle: 'Select battery condition',
    icon: Battery,
    pqc_param: 'battery_health',
    options: [
      { 
        id: 'battery_good', 
        text: 'Normal / Good',
        description: 'Battery lasts a full day',
        image: '🔋',
        pqc_option: 'bat_1',
        severity: 'Good'
      },
      { 
        id: 'battery_weak', 
        text: 'Weak / Drains fast',
        description: 'Battery drains quickly',
        image: '🪫',
        pqc_option: 'bat_2',
        severity: 'Medium'
      },
      { 
        id: 'battery_swollen', 
        text: 'Swollen / Heating',
        description: 'Battery is swollen or phone overheats',
        image: '🔥',
        pqc_option: 'bat_3',
        severity: 'High'
      }
    ]
  },
  {
    id: 'camera_condition',
    title: 'Camera working?',
    subtitle: 'Check both front and rear cameras',
    icon: Camera,
    pqc_param: 'camera',
    options: [
      { 
        id: 'camera_ok', 
        text: 'Both cameras working',
        description: 'Front and rear cameras work fine',
        image: '📷',
        pqc_options: { 'rear_camera': 'rcam_1', 'front_camera': 'fcam_1' },
        severity: 'Good'
      },
      { 
        id: 'camera_blur', 
        text: 'Blur / Focus issues',
        description: 'Camera has blur or autofocus problems',
        image: '🔍',
        pqc_options: { 'rear_camera': 'rcam_2', 'front_camera': 'fcam_2' },
        severity: 'Medium'
      },
      { 
        id: 'camera_not_working', 
        text: 'One or both not working',
        description: 'Camera(s) do not function',
        image: '📵',
        pqc_options: { 'rear_camera': 'rcam_3', 'front_camera': 'fcam_3' },
        severity: 'Medium'
      }
    ]
  },
  {
    id: 'speaker_mic',
    title: 'Speaker & Microphone?',
    subtitle: 'Test calling and media playback',
    icon: Volume2,
    pqc_param: 'audio',
    options: [
      { 
        id: 'audio_ok', 
        text: 'All working fine',
        description: 'Speaker, mic, and receiver work',
        image: '🔊',
        pqc_options: { 'speaker': 'spk_1', 'mic': 'mic_1', 'ear_receiver': 'ear_1' },
        severity: 'Good'
      },
      { 
        id: 'audio_issues', 
        text: 'Some audio issues',
        description: 'Speaker low or mic issues',
        image: '🔇',
        pqc_options: { 'speaker': 'spk_2', 'mic': 'mic_2' },
        severity: 'Low'
      },
      { 
        id: 'audio_not_working', 
        text: 'Not working',
        description: 'Major audio component failure',
        image: '🔕',
        pqc_options: { 'speaker': 'spk_3', 'mic': 'mic_2', 'ear_receiver': 'ear_2' },
        severity: 'Medium'
      }
    ]
  },
  {
    id: 'wifi_network',
    title: 'WiFi & Network?',
    subtitle: 'Check connectivity features',
    icon: Wifi,
    pqc_param: 'network',
    options: [
      { 
        id: 'network_ok', 
        text: 'All working',
        description: 'WiFi, mobile data, Bluetooth work',
        image: '📶',
        pqc_options: { 'network_signal': 'net_1', 'wifi': 'wifi_1', 'bluetooth': 'bt_1' },
        severity: 'Good'
      },
      { 
        id: 'network_weak', 
        text: 'Weak signal',
        description: 'Signal strength issues',
        image: '📉',
        pqc_options: { 'network_signal': 'net_2', 'wifi': 'wifi_2' },
        severity: 'Medium'
      },
      { 
        id: 'network_not_working', 
        text: 'Not working',
        description: 'Network features don\'t work',
        image: '❌',
        pqc_options: { 'network_signal': 'net_3', 'wifi': 'wifi_3', 'bluetooth': 'bt_2' },
        severity: 'High'
      }
    ]
  },
  {
    id: 'body_condition',
    title: 'Body condition?',
    subtitle: 'Check for physical damage',
    icon: Box,
    pqc_param: 'physical',
    options: [
      { 
        id: 'body_excellent', 
        text: 'Like new',
        description: 'No scratches or dents',
        image: '✨',
        pqc_options: { 'body_scratches': 'bscr_1', 'body_dents': 'bdnt_1', 'back_panel': 'back_1' },
        severity: 'Good'
      },
      { 
        id: 'body_minor', 
        text: 'Minor wear',
        description: 'Small scratches from normal use',
        image: '👍',
        pqc_options: { 'body_scratches': 'bscr_2', 'body_dents': 'bdnt_1', 'back_panel': 'back_2' },
        severity: 'Low'
      },
      { 
        id: 'body_heavy', 
        text: 'Heavy wear / Dents',
        description: 'Visible scratches and dents',
        image: '😓',
        pqc_options: { 'body_scratches': 'bscr_3', 'body_dents': 'bdnt_3', 'back_panel': 'back_3' },
        severity: 'Medium'
      },
      { 
        id: 'body_broken', 
        text: 'Broken back / Frame',
        description: 'Back panel cracked or frame bent',
        image: '💔',
        pqc_options: { 'back_panel': 'back_4', 'frame': 'frame_4' },
        severity: 'High'
      }
    ]
  },
  {
    id: 'accessories',
    title: 'Accessories available?',
    subtitle: 'Original accessories add value',
    icon: Package,
    pqc_param: 'packaging',
    options: [
      { 
        id: 'acc_all', 
        text: 'Box + Charger + Cable + Bill',
        description: 'All original accessories with bill',
        image: '📦',
        pqc_options: { 'box': 'box_2', 'charger': 'chrg_2', 'cable': 'cbl_2', 'bill': 'bill_2' },
        severity: 'Good',
        topup: 350
      },
      { 
        id: 'acc_partial', 
        text: 'Box + Charger',
        description: 'Original box and charger only',
        image: '🔌',
        pqc_options: { 'box': 'box_2', 'charger': 'chrg_2' },
        severity: 'Good',
        topup: 250
      },
      { 
        id: 'acc_charger', 
        text: 'Charger only',
        description: 'Only have original charger',
        image: '🔋',
        pqc_options: { 'charger': 'chrg_2' },
        severity: 'Good',
        topup: 150
      },
      { 
        id: 'acc_none', 
        text: 'Phone only',
        description: 'No accessories available',
        image: '📱',
        pqc_options: { 'box': 'box_1', 'charger': 'chrg_1' },
        severity: 'Good',
        topup: 0
      }
    ]
  },
  {
    id: 'warranty_age',
    title: 'Device warranty status?',
    subtitle: 'In-warranty devices get bonus value',
    icon: Shield,
    pqc_param: 'warranty_age',
    options: [
      { 
        id: 'warranty_new', 
        text: 'In Warranty (0-5 Months)',
        description: 'Device is less than 6 months old',
        image: '🆕',
        pqc_option: 'warage_1',
        severity: 'Good',
        topup_percent: 3
      },
      { 
        id: 'warranty_mid', 
        text: 'In Warranty (6-11 Months)',
        description: 'Device is 6-11 months old',
        image: '📅',
        pqc_option: 'warage_2',
        severity: 'Good',
        topup_percent: 1.5
      },
      { 
        id: 'warranty_out', 
        text: 'Out of Warranty (11+ Months)',
        description: 'Device is over 11 months old',
        image: '📆',
        pqc_option: 'warage_3',
        severity: 'Good',
        topup_percent: 0
      }
    ]
  }
];

const SellPhoneCashify = () => {
  // Flow: detect -> brand -> model -> variant -> qc_flow -> verify -> price -> address -> success
  const [currentStep, setCurrentStep] = useState('detect');
  const [qcStep, setQcStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Device detection
  const [detectedDevice, setDetectedDevice] = useState(null);
  const [useDetected, setUseDetected] = useState(false);
  
  // Selection data
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [variants, setVariants] = useState([]);
  
  // User selections
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [qcResponses, setQcResponses] = useState({});
  
  // Quote (hidden until OTP verified)
  const [quote, setQuote] = useState(null);
  const [showPrice, setShowPrice] = useState(false);
  
  // Customer verification
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  
  // Customer details
  const [customerName, setCustomerName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [pincode, setPincode] = useState('');
  const [leadId, setLeadId] = useState(null);
  
  // Attempt device detection on mount
  useEffect(() => {
    detectDevice();
    fetchBrands();
  }, []);
  
  const detectDevice = () => {
    const ua = navigator.userAgent;
    let detected = null;
    
    // Simple device detection based on user agent
    if (/iPhone/.test(ua)) {
      const match = ua.match(/iPhone\s*(\d+)/i);
      if (match) {
        detected = {
          brand: 'Apple',
          model: `iPhone ${match[1]}`,
          logo: 'https://logo.clearbit.com/apple.com'
        };
      }
    } else if (/Samsung/.test(ua)) {
      const match = ua.match(/SM-[A-Z]\d+/i);
      detected = {
        brand: 'Samsung',
        model: match ? match[0] : 'Galaxy',
        logo: 'https://logo.clearbit.com/samsung.com'
      };
    } else if (/OnePlus/.test(ua)) {
      detected = {
        brand: 'OnePlus',
        model: 'OnePlus Device',
        logo: 'https://logo.clearbit.com/oneplus.com'
      };
    } else if (/Xiaomi|Redmi|POCO/.test(ua)) {
      detected = {
        brand: 'Xiaomi',
        model: 'Xiaomi Device',
        logo: 'https://logo.clearbit.com/mi.com'
      };
    }
    
    setDetectedDevice(detected);
  };
  
  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/api/brands`);
      // Filter to phone brands and map logo_url to logo
      const allBrands = response.data.brands || [];
      
      // Remove duplicates by name (case-insensitive) and prioritize ones with proper names
      const uniqueBrands = {};
      allBrands.forEach(b => {
        const key = b.name?.toLowerCase().trim();
        // Skip invalid entries like 'iphone' (should be 'apple')
        if (key === 'iphone') return;
        
        if (!uniqueBrands[key] || (b.logo_url && !uniqueBrands[key].logo_url)) {
          uniqueBrands[key] = {
            ...b,
            logo: b.logo_url || `https://logo.clearbit.com/${key}.com`
          };
        }
      });
      
      // Filter to only major phone brands
      const phoneBrandNames = ['apple', 'samsung', 'oneplus', 'xiaomi', 'realme', 'oppo', 'vivo', 'google', 'nothing', 'motorola', 'tecno', 'infinix', 'iqoo', 'poco'];
      const phoneBrands = phoneBrandNames
        .map(name => uniqueBrands[name])
        .filter(Boolean);
      
      setBrands(phoneBrands.length > 0 ? phoneBrands : getDefaultBrands());
    } catch (err) {
      setBrands(getDefaultBrands());
    }
  };
  
  const getDefaultBrands = () => [
    { id: 'apple', name: 'Apple', logo: 'https://logo.clearbit.com/apple.com' },
    { id: 'samsung', name: 'Samsung', logo: 'https://logo.clearbit.com/samsung.com' },
    { id: 'oneplus', name: 'OnePlus', logo: 'https://logo.clearbit.com/oneplus.com' },
    { id: 'xiaomi', name: 'Xiaomi', logo: 'https://logo.clearbit.com/mi.com' },
    { id: 'realme', name: 'Realme', logo: 'https://logo.clearbit.com/realme.com' },
    { id: 'oppo', name: 'OPPO', logo: 'https://logo.clearbit.com/oppo.com' },
    { id: 'vivo', name: 'Vivo', logo: 'https://logo.clearbit.com/vivo.com' },
    { id: 'google', name: 'Google', logo: 'https://logo.clearbit.com/google.com' },
    { id: 'nothing', name: 'Nothing', logo: 'https://logo.clearbit.com/nothing.tech' },
  ];
  
  const fetchModels = async (brand) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/products?brand=${brand.id || brand.name}`);
      // Filter products to only show the selected brand
      const brandProducts = response.data.products?.filter(p => 
        p.brand?.toLowerCase() === brand.name?.toLowerCase()
      ) || [];
      if (brandProducts.length > 0) {
        setModels(brandProducts);
      } else {
        // Use default models if no products found for this brand
        setModels(getDefaultModels(brand.name));
      }
    } catch (err) {
      setModels(getDefaultModels(brand.name));
    } finally {
      setLoading(false);
    }
  };
  
  const getDefaultModels = (brandName) => {
    const modelsByBrand = {
      'Apple': [
        { id: 'iphone-15-pro-max', name: 'iPhone 15 Pro Max', image: '📱' },
        { id: 'iphone-15-pro', name: 'iPhone 15 Pro', image: '📱' },
        { id: 'iphone-15', name: 'iPhone 15', image: '📱' },
        { id: 'iphone-14-pro-max', name: 'iPhone 14 Pro Max', image: '📱' },
        { id: 'iphone-14-pro', name: 'iPhone 14 Pro', image: '📱' },
        { id: 'iphone-14', name: 'iPhone 14', image: '📱' },
        { id: 'iphone-13-pro-max', name: 'iPhone 13 Pro Max', image: '📱' },
        { id: 'iphone-13-pro', name: 'iPhone 13 Pro', image: '📱' },
        { id: 'iphone-13', name: 'iPhone 13', image: '📱' },
        { id: 'iphone-12', name: 'iPhone 12', image: '📱' },
        { id: 'iphone-11', name: 'iPhone 11', image: '📱' },
      ],
      'Samsung': [
        { id: 'galaxy-s24-ultra', name: 'Galaxy S24 Ultra', image: '📱' },
        { id: 'galaxy-s24-plus', name: 'Galaxy S24+', image: '📱' },
        { id: 'galaxy-s24', name: 'Galaxy S24', image: '📱' },
        { id: 'galaxy-s23-ultra', name: 'Galaxy S23 Ultra', image: '📱' },
        { id: 'galaxy-z-fold-5', name: 'Galaxy Z Fold 5', image: '📱', is_foldable: true },
        { id: 'galaxy-z-flip-5', name: 'Galaxy Z Flip 5', image: '📱', is_foldable: true },
        { id: 'galaxy-a54', name: 'Galaxy A54', image: '📱' },
        { id: 'galaxy-a34', name: 'Galaxy A34', image: '📱' },
      ],
      'OnePlus': [
        { id: 'oneplus-12', name: 'OnePlus 12', image: '📱' },
        { id: 'oneplus-12r', name: 'OnePlus 12R', image: '📱' },
        { id: 'oneplus-11', name: 'OnePlus 11', image: '📱' },
        { id: 'oneplus-open', name: 'OnePlus Open', image: '📱', is_foldable: true },
        { id: 'oneplus-nord-3', name: 'OnePlus Nord 3', image: '📱' },
      ],
      'Xiaomi': [
        { id: 'xiaomi-14-ultra', name: 'Xiaomi 14 Ultra', image: '📱' },
        { id: 'xiaomi-14', name: 'Xiaomi 14', image: '📱' },
        { id: 'xiaomi-13-pro', name: 'Xiaomi 13 Pro', image: '📱' },
        { id: 'redmi-note-13-pro', name: 'Redmi Note 13 Pro', image: '📱' },
      ],
      'Realme': [
        { id: 'realme-gt-5-pro', name: 'Realme GT 5 Pro', image: '📱' },
        { id: 'realme-12-pro', name: 'Realme 12 Pro+', image: '📱' },
        { id: 'realme-narzo-70', name: 'Realme Narzo 70', image: '📱' },
      ],
      'OPPO': [
        { id: 'oppo-find-x7-ultra', name: 'OPPO Find X7 Ultra', image: '📱' },
        { id: 'oppo-reno-11-pro', name: 'OPPO Reno 11 Pro', image: '📱' },
        { id: 'oppo-f25-pro', name: 'OPPO F25 Pro', image: '📱' },
      ],
      'Vivo': [
        { id: 'vivo-x100-pro', name: 'Vivo X100 Pro', image: '📱' },
        { id: 'vivo-v30-pro', name: 'Vivo V30 Pro', image: '📱' },
        { id: 'vivo-y100', name: 'Vivo Y100', image: '📱' },
      ],
      'Google': [
        { id: 'pixel-8-pro', name: 'Pixel 8 Pro', image: '📱' },
        { id: 'pixel-8', name: 'Pixel 8', image: '📱' },
        { id: 'pixel-7-pro', name: 'Pixel 7 Pro', image: '📱' },
        { id: 'pixel-fold', name: 'Pixel Fold', image: '📱', is_foldable: true },
      ],
      'Nothing': [
        { id: 'nothing-phone-2', name: 'Nothing Phone (2)', image: '📱' },
        { id: 'nothing-phone-1', name: 'Nothing Phone (1)', image: '📱' },
      ],
    };
    return modelsByBrand[brandName] || [
      { id: 'other-model', name: 'Other Model', image: '📱' },
    ];
  };
  
  const handleDetectedDevice = (accept) => {
    if (accept && detectedDevice) {
      const brand = brands.find(b => b.name.toLowerCase() === detectedDevice.brand.toLowerCase()) || 
                   { id: detectedDevice.brand.toLowerCase(), name: detectedDevice.brand, logo: detectedDevice.logo };
      setSelectedBrand(brand);
      setUseDetected(true);
      fetchModels(brand);
      setCurrentStep('model');
    } else {
      setCurrentStep('brand');
    }
  };
  
  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    setSelectedModel(null);
    setSelectedVariant(null);
    setQcResponses({});
    fetchModels(brand);
    setCurrentStep('model');
  };
  
  const handleModelSelect = (model) => {
    setSelectedModel(model);
    setSelectedVariant(null);
    setVariants([
      { id: '64gb', name: '64GB', storage: '64GB' },
      { id: '128gb', name: '128GB', storage: '128GB' },
      { id: '256gb', name: '256GB', storage: '256GB' },
      { id: '512gb', name: '512GB', storage: '512GB' },
      { id: '1tb', name: '1TB', storage: '1TB' },
    ]);
    setCurrentStep('variant');
  };
  
  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
    setQcStep(0);
    setQcResponses({});
    setCurrentStep('qc_flow');
  };
  
  // Get visible QC questions based on conditions
  const getVisibleQuestions = useCallback(() => {
    return CUSTOMER_QC_FLOW.filter(q => {
      if (!q.conditional) return true;
      const { param, notValues } = q.conditional;
      const response = qcResponses[param];
      if (!response) return true;
      return !notValues.includes(response.id);
    });
  }, [qcResponses]);
  
  const handleQcResponse = (questionId, option) => {
    setQcResponses(prev => ({
      ...prev,
      [questionId]: option
    }));
  };
  
  const handleNextQcStep = () => {
    const visibleQuestions = getVisibleQuestions();
    if (qcStep < visibleQuestions.length - 1) {
      setQcStep(qcStep + 1);
    } else {
      // All questions answered, move to verify
      setCurrentStep('verify');
    }
  };
  
  const handlePrevQcStep = () => {
    if (qcStep > 0) {
      setQcStep(qcStep - 1);
    } else {
      setCurrentStep('variant');
    }
  };
  
  // Map customer responses to PQC parameters
  const mapToPQCResponses = () => {
    const pqcResponses = {};
    
    // Set default "Good" responses for all parameters
    const defaultGoodResponses = {
      'power_status': 'pow_1',
      'phone_lock': 'lock_1',
      'imei_status': 'imei_1',
      'country_lock': 'clk_1',
      'display_condition': 'dsp_1',
      'screen_fading': 'fade_1',
      'spots_patch': 'spot_1',
      'screen_scratches': 'scr_1',
      'display_type_check': 'dtype_1',
      'paint_bubble': 'paint_1',
      'touch_panel': 'tch_1',
      'sub_screen': 'sub_1',
      'battery_health': 'bat_1',
      'battery_genuine': 'batg_1',
      'network_signal': 'net_1',
      'wifi': 'wifi_1',
      'bluetooth': 'bt_1',
      'rear_camera': 'rcam_1',
      'front_camera': 'fcam_1',
      'camera_lens': 'lens_1',
      'speaker': 'spk_1',
      'mic': 'mic_1',
      'ear_receiver': 'ear_1',
      'audio_ic': 'aic_1',
      'proximity_sensor': 'prox_1',
      'gyro_motion': 'gyro_1',
      'vibration': 'vib_1',
      'fingerprint_sensor': 'fp_1',
      'face_sensor': 'face_1',
      'volume_button': 'vol_1',
      'power_button': 'pwr_1',
      'silent_switch': 'sil_1',
      'home_button': 'home_3',
      'body_scratches': 'bscr_1',
      'body_dents': 'bdnt_1',
      'back_panel': 'back_1',
      'frame': 'frame_1',
      'screen_gap': 'gap_1',
      'hinges': 'hng_1',
      'charging_port': 'chg_1',
      'sim_tray': 'sim_1',
      'headphone_jack': 'jack_3',
      'memory_card': 'mem_3',
      'os_boot': 'boot_1',
      'restart_issue': 'rst_1',
      'box': 'box_1',
      'charger': 'chrg_1',
      'cable': 'cbl_1',
      'bill': 'bill_1',
      's_pen': 'spen_1',
      'warranty_age': 'warage_3'  // Default to out of warranty
    };
    
    Object.assign(pqcResponses, defaultGoodResponses);
    
    // Override with customer responses
    Object.entries(qcResponses).forEach(([questionId, option]) => {
      if (option.pqc_option) {
        // Single parameter mapping
        const question = CUSTOMER_QC_FLOW.find(q => q.id === questionId);
        if (question) {
          pqcResponses[question.pqc_param] = option.pqc_option;
        }
      }
      if (option.pqc_options) {
        // Multiple parameter mapping
        Object.entries(option.pqc_options).forEach(([param, value]) => {
          pqcResponses[param] = value;
        });
      }
    });
    
    return pqcResponses;
  };
  
  const handleSendOTP = async () => {
    if (mobile.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      
      // Call the quote API in background while sending OTP
      const pqcResponses = mapToPQCResponses();
      const quotePromise = axios.post(`${API}/api/qc-config/test-calculate`, {
        responses: pqcResponses,
        mbp: getEstimatedMBP()
      });
      
      // Send OTP
      try {
        await axios.post(`${API}/api/b2c/auth/send-otp`, { mobile_number: mobile });
      } catch (otpErr) {
        // Demo mode - continue anyway
        console.log('OTP send failed, using demo mode');
      }
      
      // Wait for quote
      const quoteResponse = await quotePromise;
      setQuote(quoteResponse.data);
      
      setOtpSent(true);
    } catch (err) {
      setError('Failed to process. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const getEstimatedMBP = () => {
    // First check if selected model/variant has our_mbp set
    if (selectedVariant?.our_mbp && selectedVariant.our_mbp > 0) {
      return selectedVariant.our_mbp;
    }
    
    // Check if model has our_mbp at product level
    if (selectedModel?.our_mbp && selectedModel.our_mbp > 0) {
      return selectedModel.our_mbp;
    }
    
    // Check for max_competitor_mbp as fallback
    if (selectedModel?.max_competitor_mbp && selectedModel.max_competitor_mbp > 0) {
      // Apply 5% margin on competitor max price
      return Math.round(selectedModel.max_competitor_mbp * 0.95);
    }
    
    // Fallback to brand-based estimate
    const mbpByBrand = {
      'Apple': 50000,
      'Samsung': 35000,
      'OnePlus': 25000,
      'Google': 30000,
      'Xiaomi': 15000,
      'Realme': 12000,
      'OPPO': 15000,
      'Vivo': 15000,
      'Nothing': 20000
    };
    return mbpByBrand[selectedBrand?.name] || 20000;
  };
  
  const handleVerifyOTP = async () => {
    if (otp.length < 4) {
      setError('Please enter valid OTP');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      
      try {
        const response = await axios.post(`${API}/api/b2c/auth/verify-otp`, {
          mobile_number: mobile,
          otp: otp
        });
        if (response.data.verified) {
          setOtpVerified(true);
        }
      } catch (verifyErr) {
        // Demo mode - accept any OTP
        console.log('OTP verify failed, using demo mode');
      }
      
      setOtpVerified(true);
      setShowPrice(true);
      setCurrentStep('price');
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateLead = async () => {
    if (!customerName || !address || !city || !pincode) {
      setError('Please fill all required fields');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      
      const pqcResponses = mapToPQCResponses();
      
      // Get display_type from product master (OLED or INCELL)
      const displayType = selectedModel?.display_type || 'OLED';
      
      // Update display_type_check in PQC responses based on product master
      if (displayType === 'INCELL') {
        pqcResponses['display_type_check'] = 'dtype_2'; // INCELL
      } else {
        pqcResponses['display_type_check'] = 'dtype_1'; // OLED (default)
      }
      
      const response = await axios.post(`${API}/api/buyback/leads`, {
        source_type: 'Website',
        source_name: 'Zobox',
        device_info: {
          brand: selectedBrand.name,
          model: selectedModel.name || selectedModel.model_name,
          variant: selectedVariant?.name,
          storage: selectedVariant?.storage,
          display_type: displayType, // From Product Master
          sku_code: selectedModel?.sku_code,
          product_id: selectedModel?.id
        },
        customer_info: {
          name: customerName,
          mobile: mobile,
          address: address,
          city: city,
          pincode: pincode
        },
        pqc_responses: pqcResponses,
        quoted_price: quote?.final_price || 0,
        quoted_grade: quote?.grade || 'Z4',
        pickup_required: true,
        pickup_address: address,
        pickup_city: city,
        pickup_pincode: pincode
      });
      
      setLeadId(response.data.lead_id);
      setCurrentStep('success');
    } catch (err) {
      setError('Failed to create request. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const goBack = () => {
    switch (currentStep) {
      case 'brand':
        setCurrentStep('detect');
        break;
      case 'model':
        setCurrentStep('brand');
        break;
      case 'variant':
        setCurrentStep('model');
        break;
      case 'qc_flow':
        if (qcStep > 0) {
          handlePrevQcStep();
        } else {
          setCurrentStep('variant');
        }
        break;
      case 'verify':
        setQcStep(getVisibleQuestions().length - 1);
        setCurrentStep('qc_flow');
        break;
      case 'price':
        setCurrentStep('verify');
        setShowPrice(false);
        break;
      case 'address':
        setCurrentStep('price');
        break;
      default:
        break;
    }
  };
  
  // Render progress sidebar for QC flow
  const renderQcSidebar = () => {
    const visibleQuestions = getVisibleQuestions();
    return (
      <div className="hidden md:block w-64 bg-gray-50 border-r border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-500 mb-4 uppercase tracking-wide">Assessment Progress</h3>
        <div className="space-y-2">
          {visibleQuestions.map((q, idx) => {
            const isCompleted = qcResponses[q.id];
            const isCurrent = idx === qcStep;
            const Icon = q.icon;
            
            return (
              <div
                key={q.id}
                className={`flex items-center gap-3 p-2 rounded-lg transition-all ${
                  isCurrent ? 'bg-[#2b3380] text-white' : 
                  isCompleted ? 'bg-green-50 text-green-700' : 
                  'text-gray-400'
                }`}
              >
                {isCompleted && !isCurrent ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <Icon className="w-5 h-5" />
                )}
                <span className="text-sm font-medium truncate">{q.title.split('?')[0]}</span>
              </div>
            );
          })}
        </div>
        
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="text-xs text-gray-500 mb-2">Progress</div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-[#2b3380] h-2 rounded-full transition-all"
              style={{ width: `${((qcStep + 1) / visibleQuestions.length) * 100}%` }}
            />
          </div>
          <div className="text-xs text-gray-500 mt-1 text-right">
            {qcStep + 1} of {visibleQuestions.length}
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#2b3380] text-white py-3 px-4 shadow-lg sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {currentStep !== 'detect' && currentStep !== 'success' && (
              <button
                onClick={goBack}
                className="p-2 hover:bg-white/10 rounded-lg transition"
                data-testid="back-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <div className="flex items-center gap-2">
              <Smartphone className="w-6 h-6" />
              <span className="font-bold text-lg">Sell Your Phone</span>
            </div>
          </div>
          
          {selectedBrand && selectedModel && (
            <div className="hidden sm:flex items-center gap-2 text-sm bg-white/10 px-3 py-1.5 rounded-full">
              <span>{selectedBrand.name}</span>
              <ChevronRight className="w-4 h-4" />
              <span>{selectedModel.name}</span>
              {selectedVariant && (
                <>
                  <ChevronRight className="w-4 h-4" />
                  <span>{selectedVariant.name}</span>
                </>
              )}
            </div>
          )}
        </div>
      </header>
      
      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-3">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
      
      <main className="flex">
        {/* Sidebar for QC flow */}
        {currentStep === 'qc_flow' && renderQcSidebar()}
        
        {/* Main Content */}
        <div className="flex-1 p-4 md:p-8">
          <div className="max-w-3xl mx-auto">
            
            {/* Step: Device Detection */}
            {currentStep === 'detect' && (
              <div className="text-center py-8" data-testid="detect-step">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-[#2b3380]/10 rounded-full mb-6">
                  <Smartphone className="w-10 h-10 text-[#2b3380]" />
                </div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">Sell Your Old Phone</h1>
                <p className="text-gray-500 mb-8">Get instant cash at your doorstep</p>
                
                {detectedDevice ? (
                  <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 max-w-md mx-auto">
                    <div className="flex items-center gap-4 mb-4">
                      <img
                        src={detectedDevice.logo}
                        alt={detectedDevice.brand}
                        className="w-12 h-12 object-contain"
                        onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${detectedDevice.brand}&background=2b3380&color=fff`; }}
                      />
                      <div className="text-left">
                        <p className="text-sm text-gray-500">We detected</p>
                        <p className="font-bold text-lg text-gray-800">{detectedDevice.brand} {detectedDevice.model}</p>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4">Do you want to sell this phone?</p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleDetectedDevice(true)}
                        className="flex-1 py-3 bg-[#2b3380] text-white rounded-xl font-semibold hover:bg-[#1e2560] transition"
                        data-testid="yes-this-phone"
                      >
                        Yes, this phone
                      </button>
                      <button
                        onClick={() => handleDetectedDevice(false)}
                        className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-[#2b3380] transition"
                        data-testid="different-phone"
                      >
                        Different phone
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setCurrentStep('brand')}
                    className="bg-[#2b3380] text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-[#1e2560] transition shadow-lg"
                    data-testid="start-selling"
                  >
                    Start Selling
                  </button>
                )}
                
                {/* Trust badges */}
                <div className="grid grid-cols-3 gap-4 mt-12 max-w-lg mx-auto">
                  <div className="text-center">
                    <Shield className="w-8 h-8 mx-auto text-green-600 mb-2" />
                    <p className="text-sm text-gray-600">Safe & Secure</p>
                  </div>
                  <div className="text-center">
                    <Clock className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                    <p className="text-sm text-gray-600">Quick Pickup</p>
                  </div>
                  <div className="text-center">
                    <Banknote className="w-8 h-8 mx-auto text-green-600 mb-2" />
                    <p className="text-sm text-gray-600">Instant Payment</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step: Brand Selection */}
            {currentStep === 'brand' && (
              <div data-testid="brand-step">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Select Your Phone Brand</h2>
                <p className="text-gray-500 mb-6">Choose from popular brands</p>
                
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                  {brands.map(brand => (
                    <button
                      key={brand.id}
                      onClick={() => handleBrandSelect(brand)}
                      className="bg-white p-4 rounded-xl border-2 border-gray-100 hover:border-[#2b3380] hover:shadow-lg transition-all flex flex-col items-center group"
                      data-testid={`brand-${brand.name?.toLowerCase()}`}
                    >
                      <img
                        src={brand.logo || `https://logo.clearbit.com/${brand.name?.toLowerCase()}.com`}
                        alt={brand.name}
                        className="w-12 h-12 object-contain mb-2 group-hover:scale-110 transition-transform"
                        onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${brand.name}&background=2b3380&color=fff`; }}
                      />
                      <span className="text-sm font-medium text-gray-700">{brand.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Step: Model Selection */}
            {currentStep === 'model' && (
              <div data-testid="model-step">
                <div className="flex items-center gap-3 mb-6">
                  <img
                    src={selectedBrand?.logo}
                    alt={selectedBrand?.name}
                    className="w-10 h-10 object-contain"
                    onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${selectedBrand?.name}&background=2b3380&color=fff`; }}
                  />
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">Select {selectedBrand?.name} Model</h2>
                    <p className="text-gray-500">Choose your phone model</p>
                  </div>
                </div>
                
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2b3380]"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {models.map(model => (
                      <button
                        key={model.id}
                        onClick={() => handleModelSelect(model)}
                        className="bg-white p-4 rounded-xl border-2 border-gray-100 hover:border-[#2b3380] hover:bg-[#f0f4ff] transition-all flex items-center justify-between text-left group"
                        data-testid={`model-${model.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{model.image || '📱'}</span>
                          <span className="font-medium text-gray-800">{model.name || model.model_name}</span>
                          {model.is_foldable && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Foldable</span>
                          )}
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#2b3380]" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Step: Variant Selection */}
            {currentStep === 'variant' && (
              <div data-testid="variant-step">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Select Storage</h2>
                <p className="text-gray-500 mb-6">
                  {selectedBrand?.name} {selectedModel?.name}
                </p>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {variants.map(variant => (
                    <button
                      key={variant.id}
                      onClick={() => handleVariantSelect(variant)}
                      className="bg-white p-6 rounded-xl border-2 border-gray-100 hover:border-[#2b3380] hover:bg-[#f0f4ff] transition-all text-center font-semibold text-lg"
                      data-testid={`variant-${variant.id}`}
                    >
                      {variant.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Step: QC Flow - Multi-page questionnaire */}
            {currentStep === 'qc_flow' && (
              <div data-testid="qc-flow-step">
                {(() => {
                  const visibleQuestions = getVisibleQuestions();
                  const currentQuestion = visibleQuestions[qcStep];
                  const Icon = currentQuestion?.icon || Info;
                  const isAnswered = qcResponses[currentQuestion?.id];
                  
                  return (
                    <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
                      {/* Mobile progress */}
                      <div className="md:hidden mb-6">
                        <div className="flex justify-between text-sm text-gray-500 mb-2">
                          <span>Question {qcStep + 1} of {visibleQuestions.length}</span>
                          <span>{Math.round(((qcStep + 1) / visibleQuestions.length) * 100)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-[#2b3380] h-2 rounded-full transition-all"
                            style={{ width: `${((qcStep + 1) / visibleQuestions.length) * 100}%` }}
                          />
                        </div>
                      </div>
                      
                      {/* Question Header */}
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-14 h-14 bg-[#2b3380]/10 rounded-xl flex items-center justify-center">
                          <Icon className="w-7 h-7 text-[#2b3380]" />
                        </div>
                        <div>
                          <h2 className="text-xl md:text-2xl font-bold text-gray-800">{currentQuestion?.title}</h2>
                          <p className="text-gray-500">{currentQuestion?.subtitle}</p>
                        </div>
                      </div>
                      
                      {/* Options Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                        {currentQuestion?.options.map(option => {
                          const isSelected = qcResponses[currentQuestion.id]?.id === option.id;
                          return (
                            <button
                              key={option.id}
                              onClick={() => handleQcResponse(currentQuestion.id, option)}
                              className={`p-4 rounded-xl border-2 transition-all text-left ${
                                isSelected 
                                  ? 'border-[#2b3380] bg-[#2b3380]/5 shadow-md' 
                                  : 'border-gray-200 hover:border-[#2b3380]/50 hover:bg-gray-50'
                              }`}
                              data-testid={`option-${option.id}`}
                            >
                              <div className="flex items-start gap-3">
                                <span className="text-3xl">{option.image}</span>
                                <div className="flex-1">
                                  <p className={`font-semibold ${isSelected ? 'text-[#2b3380]' : 'text-gray-800'}`}>
                                    {option.text}
                                  </p>
                                  <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                                  {option.topup > 0 && (
                                    <p className="text-xs text-green-600 mt-1 font-medium">+₹{option.topup} bonus</p>
                                  )}
                                </div>
                                {isSelected && (
                                  <CheckCircle className="w-6 h-6 text-[#2b3380]" />
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      
                      {/* Navigation */}
                      <div className="flex gap-4">
                        <button
                          onClick={handlePrevQcStep}
                          className="px-6 py-3 border-2 border-gray-300 rounded-xl font-semibold text-gray-700 hover:border-gray-400 transition"
                        >
                          Back
                        </button>
                        <button
                          onClick={handleNextQcStep}
                          disabled={!isAnswered}
                          className={`flex-1 py-3 rounded-xl font-semibold text-lg transition ${
                            isAnswered
                              ? 'bg-[#2b3380] text-white hover:bg-[#1e2560]'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                          data-testid="next-btn"
                        >
                          {qcStep < visibleQuestions.length - 1 ? 'Next' : 'Get Price'}
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
            
            {/* Step: Verify Mobile (Price Hidden) */}
            {currentStep === 'verify' && (
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 max-w-md mx-auto" data-testid="verify-step">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#2b3380]/10 rounded-full mb-4">
                    <Phone className="w-8 h-8 text-[#2b3380]" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800">Verify to See Price</h2>
                  <p className="text-gray-500 mt-2">Enter your mobile number to get the best price for your device</p>
                </div>
                
                {/* Device Summary */}
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">📱</span>
                    <div>
                      <p className="font-semibold text-gray-800">
                        {selectedBrand?.name} {selectedModel?.name}
                      </p>
                      <p className="text-sm text-gray-500">{selectedVariant?.name}</p>
                    </div>
                  </div>
                </div>
                
                {/* Mobile Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number</label>
                  <div className="flex">
                    <span className="inline-flex items-center px-4 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl text-gray-600 font-medium">
                      +91
                    </span>
                    <input
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="Enter 10-digit number"
                      className="flex-1 p-3 border border-gray-300 rounded-r-xl focus:ring-2 focus:ring-[#2b3380] focus:border-transparent text-lg"
                      disabled={otpSent}
                      data-testid="mobile-input"
                    />
                  </div>
                </div>
                
                {!otpSent ? (
                  <button
                    onClick={handleSendOTP}
                    disabled={mobile.length !== 10 || loading}
                    className={`w-full py-4 rounded-xl font-semibold text-lg transition ${
                      mobile.length === 10
                        ? 'bg-[#2b3380] text-white hover:bg-[#1e2560]'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                    data-testid="send-otp-btn"
                  >
                    {loading ? 'Sending...' : 'Get OTP'}
                  </button>
                ) : (
                  <>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Enter OTP</label>
                      <input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6-digit OTP"
                        className="w-full p-3 border border-gray-300 rounded-xl text-center text-2xl tracking-[0.5em] font-mono focus:ring-2 focus:ring-[#2b3380]"
                        data-testid="otp-input"
                      />
                    </div>
                    <button
                      onClick={handleVerifyOTP}
                      disabled={otp.length < 4 || loading}
                      className={`w-full py-4 rounded-xl font-semibold text-lg transition ${
                        otp.length >= 4
                          ? 'bg-[#2b3380] text-white hover:bg-[#1e2560]'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                      data-testid="verify-otp-btn"
                    >
                      {loading ? 'Verifying...' : 'Verify & See Price'}
                    </button>
                    <button
                      onClick={() => { setOtpSent(false); setOtp(''); }}
                      className="w-full mt-3 py-2 text-[#2b3380] text-sm hover:underline"
                    >
                      Change Number
                    </button>
                  </>
                )}
              </div>
            )}
            
            {/* Step: Price Reveal */}
            {currentStep === 'price' && showPrice && quote && (
              <div className="text-center" data-testid="price-step">
                <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 max-w-md mx-auto">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">Your Price is Ready!</h2>
                  <p className="text-gray-500 mb-6">
                    {selectedBrand?.name} {selectedModel?.name} ({selectedVariant?.name})
                  </p>
                  
                  {/* Price Display */}
                  <div className="bg-gradient-to-r from-[#2b3380] to-[#4a5bc7] rounded-2xl p-6 text-white mb-6">
                    <p className="text-sm opacity-80 mb-1">Get Upto</p>
                    <p className="text-5xl font-bold">₹{quote.final_price?.toLocaleString()}</p>
                    <div className="mt-3 flex items-center justify-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        quote.grade === 'Z1' ? 'bg-green-500' :
                        quote.grade === 'Z2' ? 'bg-green-400' :
                        quote.grade === 'Z3' ? 'bg-blue-400' :
                        quote.grade === 'Z4' ? 'bg-blue-300' :
                        quote.grade === 'Z5' ? 'bg-yellow-400 text-gray-800' :
                        quote.grade === 'Z6' ? 'bg-orange-400' :
                        'bg-red-400'
                      }`}>
                        {quote.grade_name || quote.grade}
                      </span>
                    </div>
                  </div>
                  
                  {/* Price Breakdown */}
                  {quote.deductions?.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                      <p className="font-semibold text-gray-800 mb-3">Price Breakdown</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Maximum Price</span>
                          <span className="font-medium">₹{quote.mbp?.toLocaleString()}</span>
                        </div>
                        {quote.deductions.slice(0, 3).map((d, i) => (
                          <div key={i} className="flex justify-between text-red-600">
                            <span className="truncate flex-1 mr-2">{d.parameter}</span>
                            <span>-₹{d.amount?.toLocaleString()}</span>
                          </div>
                        ))}
                        {quote.deductions.length > 3 && (
                          <div className="text-gray-500 text-xs">
                            +{quote.deductions.length - 3} more deductions
                          </div>
                        )}
                        {quote.total_topup > 0 && (
                          <div className="flex justify-between text-green-600">
                            <span>Accessories Bonus</span>
                            <span>+₹{quote.total_topup?.toLocaleString()}</span>
                          </div>
                        )}
                        <div className="border-t pt-2 flex justify-between font-bold">
                          <span>Your Price</span>
                          <span className="text-green-600">₹{quote.final_price?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={() => setCurrentStep('address')}
                    className="w-full py-4 bg-[#2b3380] text-white rounded-xl font-semibold text-lg hover:bg-[#1e2560] transition shadow-lg"
                    data-testid="proceed-btn"
                  >
                    Schedule Free Pickup
                  </button>
                  
                  <p className="text-xs text-gray-500 mt-4">
                    Final price may vary after physical inspection
                  </p>
                </div>
              </div>
            )}
            
            {/* Step: Address Form */}
            {currentStep === 'address' && (
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 max-w-md mx-auto" data-testid="address-step">
                <div className="text-center mb-6">
                  <MapPin className="w-12 h-12 text-[#2b3380] mx-auto mb-3" />
                  <h2 className="text-2xl font-bold text-gray-800">Pickup Details</h2>
                  <p className="text-gray-500">We'll pick up your device for free</p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 inline mr-1" /> Full Name *
                    </label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Enter your name"
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#2b3380]"
                      data-testid="name-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="w-4 h-4 inline mr-1" /> Pickup Address *
                    </label>
                    <textarea
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Enter complete address"
                      rows={3}
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#2b3380]"
                      data-testid="address-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                      <input
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="City"
                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#2b3380]"
                        data-testid="city-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Pincode *</label>
                      <input
                        type="text"
                        value={pincode}
                        onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Pincode"
                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#2b3380]"
                        data-testid="pincode-input"
                      />
                    </div>
                  </div>
                  
                  {/* Quote Summary */}
                  <div className="bg-green-50 rounded-xl p-4 mt-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-600">Your Quote</p>
                        <p className="text-2xl font-bold text-green-600">₹{quote?.final_price?.toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">{selectedBrand?.name}</p>
                        <p className="text-sm text-gray-500">{selectedModel?.name}</p>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleCreateLead}
                    disabled={loading || !customerName || !address || !city || !pincode}
                    className={`w-full py-4 rounded-xl font-semibold text-lg transition ${
                      customerName && address && city && pincode
                        ? 'bg-[#2b3380] text-white hover:bg-[#1e2560] shadow-lg'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                    data-testid="submit-btn"
                  >
                    {loading ? 'Submitting...' : 'Confirm Pickup'}
                  </button>
                </div>
              </div>
            )}
            
            {/* Step: Success */}
            {currentStep === 'success' && (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md mx-auto" data-testid="success-step">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Pickup Scheduled!</h2>
                <p className="text-gray-500 mb-6">Our team will contact you within 24 hours</p>
                
                <div className="bg-gray-50 rounded-xl p-6 mb-6 text-left">
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Lead ID:</strong> {leadId}
                  </p>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Device:</strong> {selectedBrand?.name} {selectedModel?.name}
                  </p>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Quote:</strong> ₹{quote?.final_price?.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Mobile:</strong> +91 {mobile}
                  </p>
                </div>
                
                <div className="bg-blue-50 rounded-xl p-4 mb-6 text-left">
                  <p className="text-sm text-blue-800 font-medium mb-2">What's Next?</p>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>✓ Our pickup executive will call you</li>
                    <li>✓ Device inspection at your doorstep</li>
                    <li>✓ Instant payment via UPI/Cash</li>
                  </ul>
                </div>
                
                <button
                  onClick={() => window.location.reload()}
                  className="px-8 py-3 bg-[#2b3380] text-white rounded-xl font-semibold hover:bg-[#1e2560] transition"
                >
                  Sell Another Device
                </button>
              </div>
            )}
            
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="bg-gray-100 py-6 mt-8">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>© 2024 Zobox Protech Limited. All rights reserved.</p>
          <p className="mt-1">Trusted by 1,00,000+ customers across India</p>
        </div>
      </footer>
    </div>
  );
};

export default SellPhoneCashify;
